import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTriangleExclamation, faCalendarXmark } from '@fortawesome/free-solid-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { countryFromTz, ianaOf, isIana, cityOf } from '../Admin/Studentzone';
import { makeShortNames } from '../utils/studentNames';
import './LiveSchedule.css';

const API = process.env.REACT_APP_API_BASE_URL;

const CLASS_MIN = 60; // every class is 1 hour (same as before)
const DEFAULT_START = 8 * 60; // 8:00 AM
const DEFAULT_END = 23 * 60; // 11:00 PM (last 10 PM class ends here)

const TYPE = {
  paid: { label: 'Paid' },
  demo: { label: 'Demo' },
  group: { label: 'Group' },
};

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();
const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const hourLabel = (min) => {
  const h = Math.floor(min / 60) % 24;
  return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;
};

// Same week rule as before: Monday → Sunday, moved by weekOffset
const getWeek = (offset) => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - (now.getDay() === 0 ? 6 : now.getDay() - 1) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
};

// Same name rules as before: "First.Last" → "First L." ; groups → "Group X"
const toSession = (s, i) => {
  const start = new Date(s.ClassDateTime);
  const type = s.Type === 0 ? 'paid' : s.Type === 1 ? 'demo' : 'group';
  let title;
  if (type === 'group') {
    title = `Group ${(s.FullName || '').split(' ')[1] || ''}`.trim();
  } else {
    const [first = '', last = ''] = (s.FullName || '').split('.');
    title = `${first.trim()}${last.trim() ? ` ${last.trim().charAt(0)}.` : ''}` || 'Student';
  }
  const taken = s.sessionsTaken;
  const total = s.totalSessions;
  let countTone = '';
  if (total != null && taken === total) countTone = 'is-last';
  else if (total != null && taken === total - 1) countTone = 'is-near';
  // the student's flag + their own time (hover)
  const tz = ianaOf(s.Timezone);
  let flag = '';
  let zoneTip = '';
  if (tz && isIana(tz)) {
    flag = countryFromTz(tz);
    zoneTip = tz === 'Asia/Beirut' ? 'Lebanon' : `${cityOf(tz)} · ${start.toLocaleString('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit' })} their time`;
  }
  return {
    key: s.Id ?? `${s.ClassDateTime}-${i}`,
    start,
    startMin: minutesOf(start),
    type,
    title,
    first: s.FirstName,
    last: s.LastName,
    fullName: [s.FirstName, s.LastName].filter(Boolean).join(' ') || s.FullName || '',
    flag,
    zoneTip,
    taken,
    total,
    countTone,
  };
};

const Flag = ({ s }) => (s.flag ? <span className={`fi fi-${s.flag} rmls-flag`} role="img" aria-label={s.zoneTip} /> : null);

// Side-by-side columns when two classes overlap
const layoutDay = (items) => {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin);
  const out = [];
  let cluster = [];
  let lanes = [];
  let clusterEnd = -1;
  const close = () => {
    cluster.forEach((c) => out.push({ ...c, lanes: lanes.length }));
    cluster = [];
    lanes = [];
  };
  sorted.forEach((s) => {
    if (s.startMin >= clusterEnd) {
      close();
      clusterEnd = -1;
    }
    let lane = lanes.findIndex((end) => end <= s.startMin);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(0);
    }
    lanes[lane] = s.startMin + CLASS_MIN;
    clusterEnd = Math.max(clusterEnd, s.startMin + CLASS_MIN);
    cluster.push({ ...s, lane });
  });
  close();
  return out;
};

const Count = ({ s }) =>
  s.type === 'demo' || s.total == null ? null : (
    <span className={`rmls-count ${s.countTone}`}>
      {s.taken}/{s.total}
    </span>
  );

const LiveSchedule = ({ userData }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [now, setNow] = useState(new Date());
  const [pickedDay, setPickedDay] = useState(null); // phones only

  const teacherId = userData?.id;
  const week = useMemo(() => getWeek(weekOffset), [weekOffset]);

  // "now" line, refreshed every minute
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // ---------- Load the week (same endpoint) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/liveschedule/${teacherId}/${weekOffset}`);
        if (cancelled) return;
        const list = (res.data?.results || []).map(toSession).filter((s) => !Number.isNaN(s.start.getTime()));
        // "David Nas." / "David Noh." when two students would both be "David N."
        const short = makeShortNames(list.filter((x) => x.type !== 'group' && x.first).map((x) => ({ first: x.first, last: x.last })));
        setSessions(list.map((x) => (x.type !== 'group' && x.first ? { ...x, title: short(x.first, x.last) } : x)));
      } catch (err) {
        console.error('Error fetching schedule:', err);
        if (!cancelled) {
          setSessions([]);
          setError('We couldn’t load your schedule.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, weekOffset, reload]);

  // ---------- Put each class in its day ----------
  const days = useMemo(() => {
    const map = new Map(week.map((d) => [dayKey(d), []]));
    sessions.forEach((s) => {
      // match by date; if the server week is shifted, fall back to the weekday (old behaviour)
      const list = map.get(dayKey(s.start)) || map.get(dayKey(week[(s.start.getDay() + 6) % 7]));
      list.push(s);
    });
    return week.map((date) => ({ date, key: dayKey(date), items: layoutDay(map.get(dayKey(date))) }));
  }, [sessions, week]);

  // Visible hours: 8 AM → 11 PM, stretched if a class falls outside
  const [startMin, endMin] = useMemo(() => {
    let s = DEFAULT_START;
    let e = DEFAULT_END;
    sessions.forEach((x) => {
      s = Math.min(s, Math.floor(x.startMin / 60) * 60);
      e = Math.max(e, Math.ceil((x.startMin + CLASS_MIN) / 60) * 60);
    });
    return [s, Math.min(e, 24 * 60)];
  }, [sessions]);

  const hours = [];
  for (let m = startMin; m < endMin; m += 60) hours.push(m);

  const todayKey = dayKey(now);
  const nowMin = minutesOf(now);
  const totals = sessions.reduce(
    (acc, s) => ({ ...acc, [s.type]: acc[s.type] + 1 }),
    { paid: 0, demo: 0, group: 0 }
  );

  // Phones: which day is open
  const defaultDay = days.find((d) => d.key === todayKey) || days.find((d) => d.items.length) || days[0];
  const openDay = days.find((d) => d.key === pickedDay) || defaultDay;

  const range = `${week[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${week[6].toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;

  const goWeek = (n) => {
    setPickedDay(null);
    setWeekOffset(n);
  };

  return (
    <div className="rmls">
      {/* ---------- Week bar ---------- */}
      <div className="rmls-bar">
        <div className="rmls-nav">
          <button type="button" className="rmls-arrow" onClick={() => goWeek(weekOffset - 1)} aria-label="Previous week">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="rmls-range">
            <strong>{range}</strong>
            <span>{weekOffset === 0 ? 'This week' : weekOffset === 1 ? 'Next week' : weekOffset === -1 ? 'Last week' : `${weekOffset > 0 ? '+' : ''}${weekOffset} weeks`}</span>
          </div>
          <button type="button" className="rmls-arrow" onClick={() => goWeek(weekOffset + 1)} aria-label="Next week">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          {weekOffset !== 0 && (
            <button type="button" className="rmls-today" onClick={() => goWeek(0)}>
              Today
            </button>
          )}
        </div>

        <ul className="rmls-legend" aria-label="This week">
          {Object.keys(TYPE).map((t) => (
            <li key={t} className={`is-${t}`}>
              <i /> {TYPE[t].label} <b>{loading ? '–' : totals[t]}</b>
            </li>
          ))}
        </ul>
      </div>

      {error ? (
        <div className="rmls-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmls-btn" onClick={() => setReload((n) => n + 1)}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {/* ---------- Week grid (tablet / desktop) ---------- */}
          <div className={`rmls-cal ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
            <div className="rmls-grid" style={{ '--rmls-h': `${endMin - startMin}px` }}>
              <div className="rmls-corner" />
              {days.map((d) => (
                <div key={d.key} className={`rmls-dayhead ${d.key === todayKey ? 'is-today' : ''}`}>
                  <span>{d.date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <strong>{d.date.getDate()}</strong>
                  {d.items.length > 0 && <em>{d.items.length}</em>}
                </div>
              ))}

              <div className="rmls-axis" aria-hidden="true">
                {hours.map((m) => (
                  <span key={m} style={{ top: `${m - startMin}px` }}>
                    {hourLabel(m)}
                  </span>
                ))}
              </div>

              {days.map((d) => (
                <div key={d.key} className={`rmls-col ${d.key === todayKey ? 'is-today' : ''}`}>
                  {d.key === todayKey && nowMin >= startMin && nowMin <= endMin && (
                    <span className="rmls-now" style={{ top: `${nowMin - startMin}px` }} aria-hidden="true" />
                  )}
                  {!loading &&
                    d.items.map((s) => (
                      <div
                        key={s.key}
                        className={`rmls-event is-${s.type} ${s.lanes > 1 ? 'is-tight' : ''}`}
                        style={{
                          top: `${s.startMin - startMin}px`,
                          height: `${CLASS_MIN}px`,
                          left: `calc(${(100 / s.lanes) * s.lane}% + 3px)`,
                          width: `calc(${100 / s.lanes}% - 6px)`,
                        }}
                        title={[`${s.fullName || s.title} · ${fmtTime(s.start)} · ${TYPE[s.type].label}`, s.zoneTip].filter(Boolean).join('\n')}
                      >
                        <strong>
                          <Flag s={s} />
                          {s.title}
                        </strong>
                        <span className="rmls-event-time">{fmtTime(s.start)}</span>
                        <span className="rmls-event-foot">
                          <span className="rmls-type">{TYPE[s.type].label}</span>
                          <Count s={s} />
                        </span>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>

          {/* ---------- Day list (phones) ---------- */}
          <div className="rmls-mobile">
            <div className="rmls-days" role="tablist" aria-label="Day">
              {days.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  role="tab"
                  aria-selected={openDay.key === d.key}
                  className={`${openDay.key === d.key ? 'is-on' : ''} ${d.key === todayKey ? 'is-today' : ''}`}
                  onClick={() => setPickedDay(d.key)}
                >
                  <span>{d.date.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                  <strong>{d.date.getDate()}</strong>
                  <i className={d.items.length ? 'has' : ''} aria-label={`${d.items.length} classes`} />
                </button>
              ))}
            </div>

            {loading ? (
              [0, 1, 2].map((i) => <div key={i} className="rmls-skel" />)
            ) : openDay.items.length === 0 ? (
              <div className="rmls-empty">
                <FontAwesomeIcon icon={faCalendarXmark} />
                <p>No classes on {openDay.date.toLocaleDateString('en-US', { weekday: 'long' })}.</p>
              </div>
            ) : (
              <ul className="rmls-list">
                {[...openDay.items]
                  .sort((a, b) => a.startMin - b.startMin)
                  .map((s) => {
                    const end = new Date(s.start.getTime() + CLASS_MIN * 60000);
                    return (
                      <li key={s.key} className={`rmls-item is-${s.type}`}>
                        <div className="rmls-item-time">
                          <strong>{fmtTime(s.start)}</strong>
                          <span>{fmtTime(end)}</span>
                        </div>
                        <div className="rmls-item-main">
                          <strong>
                            <Flag s={s} />
                            {s.title}
                          </strong>
                          <span className="rmls-type">{TYPE[s.type].label}</span>
                          {s.zoneTip && s.zoneTip !== 'Lebanon' && <span className="rmls-zone">{s.zoneTip}</span>}
                        </div>
                        {s.type !== 'demo' && s.total != null && (
                          <div className="rmls-item-count">
                            <Count s={s} />
                            <span>sessions</span>
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LiveSchedule;